"use client";

import Link from "next/link";
import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import type { ExploreSkillCard } from "../types";
import { getSkillCategoryEmoji } from "./skill-category-emoji";

const SKILL_MONO_FONT =
  'ui-monospace, "SFMono-Regular", "SF Mono", Menlo, Consolas, "Liberation Mono", monospace';

type SortOption = "newest" | "stars" | "name";

const SORT_OPTIONS: Array<{ value: SortOption; label: string }> = [
  { value: "newest", label: "Newest" },
  { value: "stars", label: "Stars" },
  { value: "name", label: "A-Z" },
];

function formatStars({ count }: { count: number | null }): string | null {
  if (count === null) {
    return null;
  }
  if (count >= 1000) {
    return `${(count / 1000).toFixed(1).replace(/\.0$/, "")}k`;
  }
  return `${count}`;
}

function collectCategories({
  skills,
}: {
  skills: readonly ExploreSkillCard[];
}): Array<{ name: string; count: number }> {
  const counts = new Map<string, number>();
  for (const skill of skills) {
    const seen = new Set<string>();
    for (const category of skill.categories) {
      if (seen.has(category)) {
        continue;
      }
      seen.add(category);
      counts.set(category, (counts.get(category) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function NewBadge() {
  return (
    <span className="inline-flex items-center rounded-sm bg-[var(--explore-badge-release-bg)] px-2 py-0.5 font-semibold text-[0.6rem] text-[var(--explore-badge-release-text)] uppercase tracking-[0.05em]">
      New
    </span>
  );
}

function StarIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-3 w-3"
      fill="currentColor"
      viewBox="0 0 16 16"
    >
      <path d="M8 .25a.75.75 0 01.673.418l1.882 3.815 4.21.612a.75.75 0 01.416 1.279l-3.046 2.97.719 4.192a.75.75 0 01-1.088.791L8 12.347l-3.766 1.98a.75.75 0 01-1.088-.79l.72-4.194L.818 6.374a.75.75 0 01.416-1.28l4.21-.611L7.327.668A.75.75 0 018 .25z" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4 text-[var(--explore-text-muted)]"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      viewBox="0 0 24 24"
    >
      <circle cx="11" cy="11" r="8" />
      <path d="M21 21l-4.35-4.35" strokeLinecap="round" />
    </svg>
  );
}

function BackArrowIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      viewBox="0 0 24 24"
    >
      <path
        d="M19 12H5M12 19l-7-7 7-7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function FilterIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      viewBox="0 0 24 24"
    >
      <path
        d="M4 21V14M4 10V3M12 21V12M12 8V3M20 21V16M20 12V3M1 14h6M9 8h6M17 16h6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-3.5 w-3.5"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      viewBox="0 0 24 24"
    >
      <path
        d="M18 6L6 18M6 6l12 12"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SkillGridCard({
  publicExplorePrefix,
  skill,
}: {
  publicExplorePrefix: string;
  skill: ExploreSkillCard;
}) {
  const emoji = getSkillCategoryEmoji({ category: skill.primaryCategory });
  const categoryLabel = skill.primaryCategory ?? "Skill";
  const starLabel = formatStars({ count: skill.stars });

  return (
    <Link
      className="group hover:-translate-y-px flex h-full flex-col gap-2.5 rounded-2xl border border-[color-mix(in_srgb,var(--explore-accent-yellow)_50%,var(--explore-border-light))] bg-[var(--explore-bg-card)] p-4 no-underline transition-all hover:border-[var(--explore-accent-yellow)] active:scale-[0.995]"
      href={`${publicExplorePrefix}/skills/${skill.slug}`}
      style={{
        fontFamily: "var(--homepage-sans-stack)",
        boxShadow: "var(--explore-card-shadow)",
      }}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="truncate text-[0.72rem] text-[var(--explore-text-muted)]">
          {emoji ? `${emoji} ` : ""}
          {categoryLabel}
        </span>
        <div className="flex shrink-0 items-center gap-1.5">
          {skill.isNew ? <NewBadge /> : null}
        </div>
      </div>

      <h3
        className="text-pretty font-semibold text-[0.95rem] text-[var(--explore-text-primary)] leading-[1.3]"
        style={{ fontFamily: SKILL_MONO_FONT }}
      >
        {skill.name}
      </h3>

      <p className="line-clamp-2 text-[0.82rem] text-[var(--explore-text-primary)] leading-[1.5]">
        {skill.description}
      </p>

      <div className="mt-auto flex items-center justify-between gap-2 text-[0.72rem] text-[var(--explore-text-muted)]">
        <span className="truncate">
          by {skill.author} · {skill.timeAgo}
        </span>
        {starLabel ? (
          <span className="inline-flex shrink-0 items-center gap-1 font-medium text-[var(--explore-text-secondary)]">
            <StarIcon />
            {starLabel}
          </span>
        ) : null}
      </div>
    </Link>
  );
}

function SidebarSectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-semibold text-[0.7rem] text-[var(--explore-text-muted)] uppercase tracking-[0.1em]">
      {children}
    </div>
  );
}

function CategoryChip({
  count,
  emoji,
  label,
  onToggle,
  selected,
}: {
  count: number;
  emoji: string | null;
  label: string;
  onToggle: () => void;
  selected: boolean;
}) {
  return (
    <button
      className={cn(
        "flex w-full items-center justify-between gap-2 rounded-full border-0 px-3.5 py-2 text-left font-medium text-[0.85rem] transition-all",
        selected
          ? "bg-[var(--explore-chip-selected-bg)] text-[var(--explore-chip-selected-text)] hover:opacity-90"
          : "bg-[var(--explore-tag-bg)] text-[var(--explore-text-primary)] hover:opacity-80"
      )}
      onClick={onToggle}
      type="button"
    >
      <span className="flex min-w-0 items-center gap-2">
        {emoji ? (
          <span aria-hidden="true" className="shrink-0 text-[0.95rem]">
            {emoji}
          </span>
        ) : null}
        <span className="truncate">{label}</span>
      </span>
      <span
        className={cn(
          "shrink-0 text-[0.78rem]",
          selected
            ? "text-[var(--explore-chip-selected-text)] opacity-70"
            : "text-[var(--explore-text-muted)]"
        )}
      >
        {count}
      </span>
    </button>
  );
}

function MobileSkillsFilter({
  categories,
  filteredCount,
  hasAnyFilter,
  onCategoryToggle,
  onResetAll,
  onSearchChange,
  onSortChange,
  searchQuery,
  selectedCategories,
  sortBy,
}: {
  categories: Array<{ name: string; count: number }>;
  filteredCount: number;
  hasAnyFilter: boolean;
  onCategoryToggle: (params: { category: string }) => void;
  onResetAll: () => void;
  onSearchChange: (value: string) => void;
  onSortChange: (value: SortOption) => void;
  searchQuery: string;
  selectedCategories: string[];
  sortBy: SortOption;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const activeCount = selectedCategories.length + (searchQuery ? 1 : 0);

  return (
    <div className="lg:hidden">
      <div className="mb-3 flex items-center gap-2">
        <button
          className="flex h-8 items-center gap-1.5 rounded-full border-0 bg-[var(--explore-tag-bg)] px-3 py-1.5 font-medium text-[0.75rem] text-[var(--explore-tag-text)] transition-all active:scale-[0.97]"
          onClick={() => setIsOpen(true)}
          type="button"
        >
          <FilterIcon />
          Filters
          {activeCount > 0 ? (
            <span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--explore-text-primary)] px-1 text-[0.6rem] text-[var(--explore-bg)]">
              {activeCount}
            </span>
          ) : null}
        </button>
        <span className="text-[0.75rem] text-[var(--explore-text-muted)]">
          {filteredCount} skill{filteredCount === 1 ? "" : "s"}
        </span>
      </div>

      {isOpen ? (
        <button
          aria-label="Close filters"
          className="fixed inset-0 z-[150] cursor-default bg-black/30"
          onClick={() => setIsOpen(false)}
          type="button"
        />
      ) : null}

      <div
        className={cn(
          "fixed right-0 bottom-0 left-0 z-[151] flex max-h-[85vh] flex-col rounded-t-[20px] bg-[var(--explore-bg-sidebar)] transition-transform duration-300",
          isOpen ? "translate-y-0" : "translate-y-full"
        )}
        style={{
          boxShadow: "0 -4px 40px rgba(0,0,0,0.15)",
          transitionTimingFunction: "cubic-bezier(0.32, 0.72, 0, 1)",
        }}
      >
        <div className="flex items-center justify-between px-5 pt-3 pb-2">
          <div className="mx-auto h-1 w-8 rounded-full bg-[var(--explore-border)]" />
          <button
            aria-label="Close"
            className="flex h-7 w-7 items-center justify-center rounded-full bg-[var(--explore-tag-bg)] text-[var(--explore-text-muted)]"
            onClick={() => setIsOpen(false)}
            type="button"
          >
            <CloseIcon />
          </button>
        </div>

        <div
          className="overflow-y-auto px-5 pb-8"
          style={{ fontFamily: "var(--homepage-sans-stack)" }}
        >
          <div className="relative mb-4">
            <div className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center">
              <SearchIcon />
            </div>
            <input
              className="w-full rounded-lg border-[var(--explore-border-light)] border-b bg-transparent py-2 pr-3 pl-8 text-[0.85rem] text-[var(--explore-text-primary)] placeholder:text-[var(--explore-text-muted)] focus:border-[var(--explore-accent-yellow)] focus:outline-none"
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Search skills..."
              type="text"
              value={searchQuery}
            />
          </div>

          <div className="border-[var(--explore-border-light)] border-t pt-4">
            <div className="mb-2">
              <SidebarSectionTitle>Sort</SidebarSectionTitle>
            </div>
            <div className="flex items-center gap-1.5">
              {SORT_OPTIONS.map((option) => (
                <button
                  className={cn(
                    "rounded-md px-3 py-1.5 font-medium text-[0.78rem] transition-all",
                    sortBy === option.value
                      ? "bg-[var(--explore-tag-bg)] text-[var(--explore-text-primary)]"
                      : "text-[var(--explore-text-muted)] hover:text-[var(--explore-text-secondary)]"
                  )}
                  key={option.value}
                  onClick={() => onSortChange(option.value)}
                  style={{ fontFamily: SKILL_MONO_FONT }}
                  type="button"
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {categories.length > 0 ? (
            <div className="border-[var(--explore-border-light)] border-t pt-4">
              <div className="mb-2 flex items-center justify-between">
                <SidebarSectionTitle>Category</SidebarSectionTitle>
                {hasAnyFilter ? (
                  <button
                    className="font-medium text-[0.7rem] text-[var(--explore-accent-yellow)] hover:underline"
                    onClick={onResetAll}
                    type="button"
                  >
                    Clear all
                  </button>
                ) : null}
              </div>
              <div className="flex flex-col gap-1.5">
                {categories.map((category) => (
                  <CategoryChip
                    count={category.count}
                    emoji={getSkillCategoryEmoji({ category: category.name })}
                    key={category.name}
                    label={category.name}
                    onToggle={() =>
                      onCategoryToggle({ category: category.name })
                    }
                    selected={selectedCategories.includes(category.name)}
                  />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function AllSkillsPage({
  publicExplorePrefix,
  skills,
}: {
  publicExplorePrefix: string;
  skills: readonly ExploreSkillCard[];
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [sortBy, setSortBy] = useState<SortOption>("newest");

  const categories = useMemo(() => collectCategories({ skills }), [skills]);

  const hasAnyFilter =
    selectedCategories.length > 0 || searchQuery.trim().length > 0;

  const filteredSkills = useMemo(() => {
    let result: readonly ExploreSkillCard[] = skills;

    if (selectedCategories.length > 0) {
      result = result.filter((skill) =>
        skill.categories.some((category) =>
          selectedCategories.includes(category)
        )
      );
    }

    if (searchQuery.trim().length > 0) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(
        (skill) =>
          skill.name.toLowerCase().includes(query) ||
          skill.description.toLowerCase().includes(query) ||
          skill.author.toLowerCase().includes(query) ||
          skill.categories.some((category) =>
            category.toLowerCase().includes(query)
          )
      );
    }

    if (sortBy === "stars") {
      result = result.toSorted((a, b) => (b.stars ?? 0) - (a.stars ?? 0));
    } else if (sortBy === "name") {
      result = result.toSorted((a, b) => a.name.localeCompare(b.name));
    } else {
      // "newest" — assume incoming order is newest first via firstSeenAt; sort defensively
      result = result.toSorted(
        (a, b) =>
          new Date(b.firstSeenAt).getTime() - new Date(a.firstSeenAt).getTime()
      );
    }

    return result;
  }, [skills, selectedCategories, searchQuery, sortBy]);

  function toggleCategory({ category }: { category: string }) {
    setSelectedCategories((previous) =>
      previous.includes(category)
        ? previous.filter((value) => value !== category)
        : [...previous, category]
    );
  }

  function resetAll() {
    setSearchQuery("");
    setSelectedCategories([]);
  }

  return (
    <div style={{ fontFamily: "var(--homepage-sans-stack)" }}>
      <div className="mx-auto max-w-[1320px] px-4 pt-6 pb-2 sm:px-6">
        <Link
          className="mb-4 inline-flex items-center gap-1.5 text-[0.8rem] text-[var(--explore-text-muted)] no-underline transition-colors hover:text-[var(--explore-text-primary)]"
          href={publicExplorePrefix}
        >
          <BackArrowIcon />
          Back to Explore
        </Link>

        <div className="flex items-start justify-between gap-4">
          <div>
            <h1
              className="mb-1 font-bold text-[1.5rem] text-[var(--explore-text-primary)] leading-tight sm:text-[1.75rem]"
              style={{ fontFamily: "var(--explore-card-title-stack)" }}
            >
              All Skills
            </h1>
            <p className="mb-4 text-[0.9rem] text-[var(--explore-text-muted)]">
              Discover AI agent skills — curated, ready to install
            </p>
          </div>

          <div className="hidden shrink-0 items-center gap-1.5 pt-1 sm:flex">
            {SORT_OPTIONS.map((option) => (
              <button
                className={cn(
                  "rounded-md px-2 py-1 font-medium text-[0.72rem] transition-all",
                  sortBy === option.value
                    ? "bg-[var(--explore-tag-bg)] text-[var(--explore-text-primary)]"
                    : "text-[var(--explore-text-muted)] hover:text-[var(--explore-text-secondary)]"
                )}
                key={option.value}
                onClick={() => setSortBy(option.value)}
                style={{ fontFamily: SKILL_MONO_FONT }}
                type="button"
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-[1320px] grid-cols-1 gap-6 px-4 pb-12 sm:px-6 lg:grid-cols-[240px_1fr]">
        <aside className="hidden flex-col gap-4 self-start rounded-2xl bg-[var(--explore-bg-sidebar)] p-4 lg:sticky lg:top-20 lg:flex">
          <div className="relative">
            <div className="pointer-events-none absolute inset-y-0 left-2.5 flex items-center">
              <SearchIcon />
            </div>
            <input
              className="w-full rounded-lg border-[var(--explore-border-light)] border-b bg-transparent py-2 pr-3 pl-8 text-[0.8rem] text-[var(--explore-text-primary)] placeholder:text-[var(--explore-text-muted)] focus:border-[var(--explore-accent-yellow)] focus:outline-none"
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search skills..."
              type="text"
              value={searchQuery}
            />
          </div>

          {categories.length > 0 ? (
            <div className="border-[var(--explore-border-light)] border-t pt-4">
              <div className="mb-3 flex items-center justify-between">
                <SidebarSectionTitle>Category</SidebarSectionTitle>
                <button
                  className={cn(
                    "font-medium text-[0.7rem]",
                    hasAnyFilter
                      ? "text-[var(--explore-accent-yellow)] hover:underline"
                      : "pointer-events-none text-[var(--explore-text-muted)] opacity-40"
                  )}
                  onClick={resetAll}
                  type="button"
                >
                  Clear all
                </button>
              </div>
              <div className="flex flex-col gap-1.5">
                {categories.map((category) => (
                  <CategoryChip
                    count={category.count}
                    emoji={getSkillCategoryEmoji({ category: category.name })}
                    key={category.name}
                    label={category.name}
                    onToggle={() => toggleCategory({ category: category.name })}
                    selected={selectedCategories.includes(category.name)}
                  />
                ))}
              </div>
            </div>
          ) : null}

          <div className="border-[var(--explore-border-light)] border-t pt-3 text-[0.75rem] text-[var(--explore-text-muted)]">
            {filteredSkills.length} skill
            {filteredSkills.length === 1 ? "" : "s"}
            {hasAnyFilter ? " matching filters" : " total"}
          </div>
        </aside>

        <MobileSkillsFilter
          categories={categories}
          filteredCount={filteredSkills.length}
          hasAnyFilter={hasAnyFilter}
          onCategoryToggle={toggleCategory}
          onResetAll={resetAll}
          onSearchChange={setSearchQuery}
          onSortChange={setSortBy}
          searchQuery={searchQuery}
          selectedCategories={selectedCategories}
          sortBy={sortBy}
        />

        <div>
          {filteredSkills.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {filteredSkills.map((skill) => (
                <SkillGridCard
                  key={skill.id}
                  publicExplorePrefix={publicExplorePrefix}
                  skill={skill}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 py-16 text-center">
              <p className="text-[1rem] text-[var(--explore-text-muted)]">
                No skills found matching your filters.
              </p>
              <button
                className="rounded-full border border-[var(--explore-border)] px-4 py-2 text-[0.8rem] text-[var(--explore-text-secondary)] transition-colors hover:border-[var(--explore-text-muted)]"
                onClick={resetAll}
                type="button"
              >
                Clear all filters
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
